/**
 * crossingOutlineSlice — CRUD de cruzamentos e outline do projeto ativo
 *
 * Consumidores: Conexoes, useOutlinePersistence (via driveProjectStore)
 * Dependências: googleDrive, outlineStore, helpers
 * Persistência: Google Drive (crossings.json e outline.json por projeto)
 */
import { googleDrive } from '../../services/googleDrive';
import { useOutlineStore } from '../outlineStore';
import { EMPTY_PROJECT_OUTLINE } from '../../types/outline';
import { ensureAuthenticated } from './helpers';
import { logger } from '../../utils/logger';
import { bigwaToast } from '../../utils/bigwaToast';
import { auditLog } from '../../utils/auditLog';
import type { SliceSet, SliceGet } from './types';

export const createCrossingOutlineSlice = (set: SliceSet, get: SliceGet) => {

  // Timestamp da última mutação local (optimistic update).
  // Usado pelos _backgroundRevalidate para evitar sobrescrever dado local
  // mais recente com dado stale vindo do Drive.
  let _lastMutationAt = 0;

  // Closure booleans para guards de revalidação (não no store para evitar re-renders)
  let _isRevalidatingCrossings = false;
  let _isRevalidatingOutline = false;

  // Background revalidation para cruzamentos
  const _backgroundRevalidateCrossings = (projectId: string) => {
    if (_isRevalidatingCrossings) return;
    _isRevalidatingCrossings = true;

    const startedAt = Date.now();

    ensureAuthenticated().then(() =>
      googleDrive.getProjectCrossings(projectId)
    ).then(freshCrossings => {
      _isRevalidatingCrossings = false;

      // Guard: só atualiza se o projeto ativo não mudou durante o fetch
      if (get().activeProjectId !== projectId) return;

      // Guard: se houve mutação local APÓS o início desta revalidação,
      // descartar a resposta do Drive para não sobrescrever dado mais recente.
      if (_lastMutationAt > startedAt) {
        logger.info('crossingOutlineSlice', 'Revalidação crossings descartada — mutação local mais recente');
        return;
      }

      if (freshCrossings !== null) {
        set({ cachedCrossings: freshCrossings, crossingsLoaded: true, crossingsLoadedAt: Date.now() });
      }
    }).catch((err) => {
      _isRevalidatingCrossings = false;
      logger.error('[crossingOutlineSlice] Revalidação crossings em background falhou:', String(err));
    });
  };

  // Background revalidation para outline
  const _backgroundRevalidateOutline = (projectId: string) => {
    if (_isRevalidatingOutline) return;
    _isRevalidatingOutline = true;

    const startedAt = Date.now();

    ensureAuthenticated().then(() =>
      googleDrive.readProjectJson(projectId)
    ).then(metadata => {
      _isRevalidatingOutline = false;

      // Guard: só atualiza se o projeto ativo não mudou durante o fetch
      if (get().activeProjectId !== projectId) return;

      // Guard: se houve mutação local APÓS o início desta revalidação,
      // descartar a resposta do Drive para não sobrescrever dado mais recente.
      if (_lastMutationAt > startedAt) {
        logger.info('crossingOutlineSlice', 'Revalidação outline descartada — mutação local mais recente');
        return;
      }

      const outline = metadata?.outline || EMPTY_PROJECT_OUTLINE;
      set({ cachedOutline: outline, outlineLoaded: true, outlineLoadedAt: Date.now() });
    }).catch((err) => {
      _isRevalidatingOutline = false;
      logger.error('[crossingOutlineSlice] Revalidação outline em background falhou:', String(err));
    });
  };

  return {
  // === FUNÇÕES DE CRUZAMENTOS ===

  // Carrega cruzamentos do projeto ativo
  loadActiveProjectCrossings: async () => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      return [];
    }

    // Se já carregou E dados são frescos (< 5 min), retorna do Zustand sem fetch
    const crossingsLoadedAt = get().crossingsLoadedAt;
    if (get().crossingsLoaded && crossingsLoadedAt && Date.now() - crossingsLoadedAt < 300000) {
      return get().cachedCrossings || [];
    }

    // Se já carregou MAS dados estão stale (≥ 5 min): serve cache instantâneo + revalida em background
    if (get().crossingsLoaded) {
      _backgroundRevalidateCrossings(activeProjectId);
      return get().cachedCrossings || [];
    }

    try {
      await ensureAuthenticated();
      const crossings = await googleDrive.getProjectCrossings(activeProjectId);
      set({ cachedCrossings: crossings, crossingsLoaded: true, crossingsLoadedAt: Date.now() });
      return crossings;
    } catch (error) {
      logger.error('DriveProjectStore', 'Erro ao carregar cruzamentos:', error);
      set({ cachedCrossings: [], crossingsLoaded: true, crossingsLoadedAt: Date.now() });
      return [];
    }
  },

  // Atualiza cruzamento no projeto ativo
  updateCrossingInActiveProject: async (tagName: string, updates: {
    customTitle?: string;
    comments?: string;
  }) => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    _lastMutationAt = Date.now();

    try {
      await ensureAuthenticated();
      await googleDrive.updateProjectCrossing(activeProjectId, tagName, updates);

      // Atualiza cache localmente
      const currentCrossings = get().cachedCrossings;
      const existingCrossing = currentCrossings.find(c => c.tagName === tagName);

      if (existingCrossing) {
        // Atualiza existente
        set(state => ({
          cachedCrossings: (state.cachedCrossings || []).map(c =>
            c.tagName === tagName ? { ...c, ...updates } : c
          ),
          crossingsLoadedAt: Date.now(),
        }));
      } else {
        // Adiciona novo
        set(state => ({
          cachedCrossings: [...state.cachedCrossings, { tagName, ...updates }],
          crossingsLoadedAt: Date.now(),
        }));
      }
      auditLog({ operation: 'update', target: 'cruzamento', targetId: tagName, detail: `Cruzamento atualizado: ${tagName}`, success: true });
    } catch (error) {
      logger.error('DriveProjectStore', 'Erro ao atualizar cruzamento:', error);
      bigwaToast.notifyError('Não foi possível salvar o cruzamento. Tente novamente.');
      auditLog({ operation: 'update', target: 'cruzamento', targetId: tagName, detail: `Falha ao atualizar cruzamento: ${tagName}`, success: false, error: String(error) });
      throw error;
    }
  },

  // Retorna cruzamentos do cache (síncrono)
  getCachedCrossings: () => {
    return get().cachedCrossings || [];
  },

  // Invalida o cache de cruzamentos
  invalidateCrossingsCache: () => {
    set({ cachedCrossings: [], crossingsLoaded: false, crossingsLoadedAt: null });
  },

  // Revalida cruzamentos em background SE dados estão stale (≥ 5 min).
  // Chamado pelo visibilitychange do DashboardLayout para sync cross-device.
  revalidateCrossingsIfStale: () => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) return;
    if (!get().crossingsLoaded) return;

    const loadedAt = get().crossingsLoadedAt;
    if (loadedAt && Date.now() - loadedAt < 300000) return;

    _backgroundRevalidateCrossings(activeProjectId);
  },

  // ============================================
  // FUNÇÕES DE OUTLINE DO PROJETO (FASE 6)
  // ============================================

  // Carrega outline do projeto ativo
  loadActiveProjectOutline: async () => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      set({ cachedOutline: null, outlineLoaded: true });
      return EMPTY_PROJECT_OUTLINE;
    }

    // Se já carregou E dados são frescos (< 5 min), retorna do Zustand sem fetch
    const outlineLoadedAt = get().outlineLoadedAt;
    if (get().outlineLoaded && outlineLoadedAt && Date.now() - outlineLoadedAt < 300000) {
      return get().cachedOutline;
    }

    // Se já carregou MAS dados estão stale (≥ 5 min): serve cache instantâneo + revalida em background
    if (get().outlineLoaded && get().cachedOutline) {
      _backgroundRevalidateOutline(activeProjectId);
      return get().cachedOutline;
    }

    try {
      await ensureAuthenticated();
      const metadata = await googleDrive.readProjectJson(activeProjectId);
      const outline = metadata.outline || EMPTY_PROJECT_OUTLINE;

      set({ cachedOutline: outline, outlineLoaded: true, outlineLoadedAt: Date.now() });
      return outline;
    } catch (error) {
      logger.error('DriveProjectStore', 'Erro ao carregar outline:', error);
      set({ cachedOutline: EMPTY_PROJECT_OUTLINE, outlineLoaded: true, outlineLoadedAt: Date.now() });
      return EMPTY_PROJECT_OUTLINE;
    }
  },

  // Salva outline do projeto ativo
  saveActiveProjectOutline: async () => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    _lastMutationAt = Date.now();

    // Pegar outline atual do outlineStore
    const outline = useOutlineStore.getState().getProjectOutline();

    try {
      await ensureAuthenticated();

      // Atualizar project.json com outline usando função específica
      await googleDrive.updateProjectOutline(activeProjectId, outline);

      // Atualizar cache local
      set({ cachedOutline: outline, outlineLoadedAt: Date.now() });

      // Marcar como salvo no outlineStore
      useOutlineStore.getState().markAsSaved();

      auditLog({ operation: 'save', target: 'outline', targetId: activeProjectId, detail: `Outline salvo`, success: true });
    } catch (error) {
      logger.error('DriveProjectStore', 'Erro ao salvar outline:', error);
      bigwaToast.notifyError('Não foi possível salvar o outline. Tente novamente.');
      auditLog({ operation: 'save', target: 'outline', targetId: activeProjectId, detail: `Falha ao salvar outline`, success: false, error: String(error) });
      throw error;
    }
  },

  // Retorna outline do cache (síncrono)
  getCachedOutline: () => {
    return get().cachedOutline;
  },

  // Invalida o cache de outline
  invalidateOutlineCache: () => {
    set({ cachedOutline: null, outlineLoaded: false, outlineLoadedAt: null });
  },

  // Revalida outline em background SE dados estão stale (≥ 5 min).
  // Chamado pelo visibilitychange do DashboardLayout para sync cross-device.
  revalidateOutlineIfStale: () => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) return;
    if (!get().outlineLoaded) return;

    const loadedAt = get().outlineLoadedAt;
    if (loadedAt && Date.now() - loadedAt < 300000) return;

    _backgroundRevalidateOutline(activeProjectId);
  },
};
};
